import type { RJSFSchema, UiSchema } from "@rjsf/utils";

interface Step {
  json_schema: RJSFSchema;
  ui_schema: UiSchema;
  name: string;
  data?: Record<string, unknown>;
  prefilled_data?: Record<string, unknown>;
}

export interface PatchMetadata {
  hasPhone?: boolean;
  isManualApplication?: boolean;
}

interface Patch {
  shouldApply: (metaData: PatchMetadata) => boolean;
  applyPatch: (step: {
    jsonSchema: RJSFSchema;
    uiSchema: UiSchema;
    data?: Record<string, unknown>;
    prefilledData?: Record<string, unknown>;
    metaData: PatchMetadata;
  }) => {
    jsonSchema: RJSFSchema;
    uiSchema: UiSchema;
    data: Record<string, unknown>;
    prefilledData: Record<string, unknown>;
    metaData: PatchMetadata;
  };
}

type StepName = string;
type PatchName = string;

// Unfortunately, some of the fields must be controlled by FE due to much easier way of extending their functionality on the FE side
const STEPS_TO_PATCH: Record<StepName, Record<PatchName, Patch>> = {
  personal_details: {
    // Control whether 'phone' or 'email' should be a required field, depending on used OTP method
    requiredPhoneOrEmail: {
      shouldApply: (_metaData) => true,
      // @ts-expect-error - rjsf types are bs
      applyPatch: ({
        jsonSchema,
        uiSchema,
        data,
        prefilledData,
        metaData
      }: {
        jsonSchema: {
          properties?: {
            contact_phone_number?: {
              minLength?: number;
            };
          };
          required: string[];
        };
        uiSchema: {
          contact_email: Record<string, unknown>;
          contact_phone_number: Record<string, unknown>;
        };
        data: { contact_phone_number?: string | null };
        prefilledData: { contact_phone_number?: string | null };
        metaData: PatchMetadata;
      }) => {
        const { hasPhone: isPhoneOtp, isManualApplication } = metaData;

        const clonedJsonSchema = structuredClone(jsonSchema);
        clonedJsonSchema.required.push(
          isPhoneOtp ? "contact_phone_number" : "contact_email"
        );
        if (
          isManualApplication &&
          clonedJsonSchema.properties?.contact_phone_number?.minLength
        ) {
          clonedJsonSchema.properties.contact_phone_number.minLength = 0;
        }

        const clonedUiSchema = structuredClone(uiSchema);
        if (isManualApplication) {
          clonedUiSchema.contact_phone_number["ui:readonly"] = false;
          clonedUiSchema.contact_email["ui:readonly"] = false;
        } else {
          clonedUiSchema.contact_phone_number["ui:readonly"] = isPhoneOtp;
          clonedUiSchema.contact_email["ui:readonly"] = !isPhoneOtp;
        }

        const clonedData = structuredClone(data);
        const clonedPrefilledData = structuredClone(prefilledData);

        if (
          Object.keys(clonedData).length &&
          !isPhoneOtp &&
          (clonedData.contact_phone_number === null ||
            clonedData.contact_phone_number === undefined)
        )
          clonedData.contact_phone_number = "";

        if (
          Object.keys(clonedPrefilledData).length &&
          !isPhoneOtp &&
          (clonedPrefilledData.contact_phone_number === null ||
            clonedPrefilledData.contact_phone_number === undefined)
        )
          clonedPrefilledData.contact_phone_number = "";

        return {
          jsonSchema: clonedJsonSchema,
          uiSchema: clonedUiSchema,
          data: clonedData,
          prefilledData: clonedPrefilledData
        };
      }
    }
  }
};

export const patchApplicantStep = ({
  step,
  patchMeta
}: {
  step: Step;
  patchMeta: PatchMetadata;
}) => {
  const {
    jsonSchema = step.json_schema,
    uiSchema = step.ui_schema,
    data = step.data,
    prefilledData = step.prefilled_data
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- it's easily may be undefined
  } = Object.entries(STEPS_TO_PATCH[step.name || ""] || {}).reduce(
    (ret, [_patchName, patch]) =>
      patch.shouldApply(patchMeta) ? patch.applyPatch(ret) : ret,
    {
      jsonSchema: step.json_schema,
      uiSchema: step.ui_schema,
      data: step.data || {},
      prefilledData: step.prefilled_data || {},
      metaData: patchMeta
    }
  );

  return {
    jsonSchema,
    uiSchema,
    data,
    prefilledData
  };
};
